import { Tag, formatDate } from "@amber/ui";
import {
  NAME_TYPE_LABELS,
  ORGANISATION_KIND_LABELS,
  ROLE_KIND_LABELS,
  type ProfileDetail,
  type Role,
  type RoleEnd,
} from "../contracts/index";

const SECTION = "m-0 mt-5 text-xs font-semibold tracking-[0.06em] text-muted uppercase";

function endLabel(end: RoleEnd): string {
  if (end.state === "ongoing") return "ongoing";
  if (end.state === "unknown") return "end unknown";
  return end.date;
}

function roleWhat(r: Pick<Role, "title" | "organisation">): string {
  if (!r.organisation) return r.title;
  return r.title === "Member" ? `Member of ${r.organisation.name}` : `${r.title}, ${r.organisation.name}`;
}

export function NamesAndRoles({ profile }: { profile: ProfileDetail }) {
  const roles = [...profile.roles].sort((a, b) => (b.start ?? "").localeCompare(a.start ?? ""));
  return (
    <>
      <h2 className={SECTION}>Roles and affiliations</h2>
      {roles.length === 0 ? (
        <p className="m-0 mt-1.5 text-md text-muted">No dated roles recorded. Unknown, not none.</p>
      ) : null}
      <ul className="m-0 mt-1.5 list-none p-0">
        {roles.map((r) => (
          <li
            key={r.id}
            className="grid grid-cols-[150px_minmax(0,1fr)_auto] items-start gap-3 border-t border-divider py-2"
          >
            <span className="font-mono text-md text-muted">
              {r.start ?? "start unknown"} – {endLabel(r.end)}
            </span>
            <span>
              <span className="block">{roleWhat(r)}</span>
              <span className="block text-xs text-muted">
                {ROLE_KIND_LABELS[r.kind]}
                {r.organisation ? ` · ${ORGANISATION_KIND_LABELS[r.organisation.kind]}` : ""} · {r.evidence}
                {r.checked_at ? ` · checked ${formatDate(r.checked_at)}` : ""}
              </span>
            </span>
            <Tag tone={r.derivation === "editor" ? "neutral" : "quiet"}>
              {r.derivation === "editor" ? "Manual · protected" : "Sourced"}
            </Tag>
          </li>
        ))}
      </ul>
      <h2 className={SECTION}>Names and aliases</h2>
      {profile.names.length === 0 ? <p className="m-0 mt-1.5 text-md text-muted">No other names recorded.</p> : null}
      <ul className="m-0 mt-1.5 list-none p-0">
        {profile.names.map((n) => (
          <li
            key={`${n.type}-${n.value}`}
            className="grid grid-cols-[150px_minmax(0,1fr)_auto] items-start gap-3 border-t border-divider py-2"
          >
            <span className="text-muted">{NAME_TYPE_LABELS[n.type]}</span>
            <span>
              <span className="block">{n.value}</span>
              <span className="block text-xs text-muted">
                {[
                  n.script !== "Latn" ? n.script : null,
                  n.language?.toUpperCase() ?? null,
                  n.valid_from || n.valid_to ? `${n.valid_from ?? "…"} – ${n.valid_to ?? "…"}` : null,
                  n.evidence,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <Tag tone={n.derivation === "editor" ? "neutral" : "quiet"}>
              {n.derivation === "editor" ? "Manual · protected" : "Sourced"}
            </Tag>
          </li>
        ))}
      </ul>
    </>
  );
}
